import type { ProductFeature } from '@/generated/productAnalytics.types'
import { router } from '@inertiajs/vue3'
import posthog from 'posthog-js'
import type { App } from 'vue'
import { dropNoisyExceptionEvents } from './errorNoise'

// Capture routes through our first-party reverse proxy (e.snitcher.com) so
// ad-blockers stop dropping events; the ui host keeps toolbar and in-app
// links pointing at the PostHog app rather than the proxy. app.ts imports
// the proxy host as its fallback so there is a single source of truth.
export const POSTHOG_PROXY_HOST = 'https://e.snitcher.com'
export const POSTHOG_UI_HOST = 'https://eu.posthog.com'

// Minimal shape of the shared `user` prop that PostHog needs — the identity
// and the current workspace whose id keys the `company` group. Kept local and
// loose so the tracker stays decoupled from the full store User type.
export interface PostHogUser {
    id: number | string
    email?: string
    name?: string
    created_at?: string
    is_admin?: boolean
    two_factor_enabled?: boolean
    current_site?: {
        id?: number | string
        subscription_status?: string
        created_at_ts?: number
        plan?: string
    } | null
}

export interface PostHogConfig {
    apiKey: string
    apiHost?: string
    enableSessionRecording?: boolean
    enableHeatmaps?: boolean
    capturePageview?: boolean
    capturePageleave?: boolean
    isImpersonating?: boolean
    // The initial page's authenticated user (props.initialPage.props.user).
    // Passed in synchronously so identity and the company group are set at
    // init time, BEFORE the first pageview — the async Inertia navigate
    // handler that used to be the only reliable group source races the
    // initial pageview and every early autocapture event.
    currentUser?: PostHogUser | null
}

let posthogInstance: typeof posthog | null = null
let impersonationActive = false

// The identity and workspace we last pushed to posthog-js. Identity setup is
// reached on EVERY Inertia navigation, so these gate it to fire only on a real
// change: identify()/group()/setPersonProperties() each cost a billable event
// (posthog-js implements setPersonProperties as an un-deduped $set), so
// re-firing them per pageview would multiply the bill. null after a reset.
let identifiedUserId: string | null = null
let groupedWebsiteId: string | null = null

// Exposed so capture paths that talk to posthog-js directly (see
// lib/errorCapture.ts) can enforce the same no-capture-while-impersonating
// invariant instead of relying on the client happening to be unbooted.
export function isImpersonationActive(): boolean {
    return impersonationActive
}

export function initPostHog(config: PostHogConfig) {
    impersonationActive = config.isImpersonating ?? false

    // Impersonated sessions must capture NOTHING to PostHog. Not booting
    // posthog-js kills every capture path at once — autocapture, pageviews,
    // session replay, heatmaps, exception capture — and critically identify(),
    // which would irreversibly merge the staff browser's device activity into
    // the customer's person record. Impersonation always starts and ends with
    // a full page load (enter: Nova redirects to /dashboard, exit: the
    // stop-impersonating URL redirects back to Nova), so this init gate is the
    // primary guard; syncImpersonationState covers the multi-tab edge where
    // the shared session flips mid-flight.
    if (impersonationActive) {
        return null
    }

    if (!config.apiKey) {
        console.warn('PostHog API key not provided, skipping initialization')
        return null
    }

    const currentUser = config.currentUser ?? null
    const currentUserId =
        currentUser?.id != null ? String(currentUser.id) : null

    // Initialize PostHog with configuration
    posthog.init(config.apiKey, {
        api_host: config.apiHost || POSTHOG_PROXY_HOST,
        ui_host: POSTHOG_UI_HOST,
        capture_pageview: config.capturePageview ?? false, // We'll handle this manually
        capture_pageleave: config.capturePageleave ?? true,
        autocapture: true, // Automatically capture clicks, form submissions, etc.
        disable_session_recording: !(config.enableSessionRecording ?? true),
        mask_all_text: false, // Don't mask text by default
        mask_all_element_attributes: false,
        enable_heatmaps: config.enableHeatmaps ?? true,
        capture_exceptions: {
            capture_unhandled_errors: true,
            capture_unhandled_rejections: true,
            capture_console_errors: false,
        },
        // Autocaptured $exception events skip lib/errorCapture.ts, so drop
        // no-signal browser/third-party noise (ResizeObserver, blocked gtag,
        // iOS webview handlers) here before it reaches the tracker.
        before_send: dropNoisyExceptionEvents,
    })

    posthogInstance = posthog

    // A full-document load (419 session-expiry redirect to /login, a shared
    // browser, a fresh tab) reboots the page, so the in-SPA logout reset below
    // never runs and posthog-js still holds the PREVIOUS user's identity and
    // $groups in persistence. Compare the persisted distinct id against the
    // current user and reset BEFORE identifying, so nothing leaks across users
    // on full-load switches. get_distinct_id() only equals a real user id when
    // _isIdentified() is true (anonymous ids are device UUIDs).
    if (
        posthog._isIdentified() &&
        posthog.get_distinct_id() !== currentUserId
    ) {
        posthog.reset()
    }

    // Every browser-originated event carries channel=web as a super property,
    // matching the backend's Laravel-Context stamp (web/api/mcp — see
    // app/Business/ProductAnalytics/Value/AnalyticsChannel.php) so that an
    // absent channel property means system-initiated across the whole project.
    // Registered after the reset above, which would otherwise wipe it.
    posthog.register({ channel: 'web' })

    // Heal a stale opt-out left behind if a previous session flipped into
    // impersonation mid-flight (see syncImpersonationState) — the opt-out
    // persists in local storage and would otherwise silence the staff
    // member's own activity forever.
    if (posthog.has_opted_out_capturing()) {
        posthog.opt_in_capturing({ captureEventName: null })
    }

    // Establish identity and the company group SYNCHRONOUSLY here, before any
    // capture. The initial page's user is passed in from app.ts, so we no
    // longer depend on window.Inertia (which the app never defines) inside an
    // async loaded callback — that was dead code, so the group was only ever
    // set once the async navigate handler ran, leaving the initial pageview
    // and early autocapture events unattributed to the workspace. A fresh boot
    // starts with no pushed identity/workspace, so identify/group fire once
    // here and the navigate handler's idempotency guard skips the redundant
    // re-fire on same-user, same-workspace navigations.
    identifiedUserId = null
    groupedWebsiteId = null
    if (currentUser?.id) {
        identifyUserWithContext(posthog, currentUser)
    }

    // Set up Inertia navigation tracking
    setupInertiaTracking()

    return posthog
}

// Identify the user and (re)establish the workspace context. The group is set
// BEFORE identify() so the $identify event itself carries the company group.
// identify() is skipped when the user is unchanged (it emits a billable event),
// so this is safe to call on every navigation.
function identifyUserWithContext(ph: typeof posthog, user: PostHogUser) {
    registerWorkspaceContext(ph, user.current_site ?? null)

    const userId = String(user.id)
    if (userId === identifiedUserId) {
        return
    }

    ph.identify(userId, {
        email: user.email,
        name: user.name,
        created_at: user.created_at,
        is_admin: user.is_admin || false,
        has_2fa_enabled: user.two_factor_enabled || false,
    })

    identifiedUserId = userId
}

// Multi-tab edge case: the impersonation session is shared browser-wide, so a
// tab that booted PostHog as the staff member can see `admin.is_impersonating`
// appear on an Inertia response when the staff impersonates from another tab.
// Opting out stops autocapture, replay, and captures without a reload;
// opting back in (silently) restores normal tracking once impersonation ends.
function syncImpersonationState(isImpersonating: boolean) {
    if (isImpersonating === impersonationActive) {
        return
    }
    impersonationActive = isImpersonating

    if (!posthogInstance) {
        return
    }
    if (isImpersonating) {
        posthogInstance.opt_out_capturing()
    } else if (posthogInstance.has_opted_out_capturing()) {
        posthogInstance.opt_in_capturing({ captureEventName: null })
    }
}

// (Re)establish the company group and workspace context. The group/website_id
// block is guarded on a real workspace switch: group() emits a $groupidentify
// and setPersonProperties() an un-deduped $set, so re-firing them on every
// same-workspace navigation would multiply the event bill.
function registerWorkspaceContext(ph: typeof posthog, site: any) {
    if (!site) return

    if (site.id) {
        const websiteId = String(site.id)

        if (websiteId !== groupedWebsiteId) {
            // Same company-group key the backend stamps (PostHogService uses
            // the raw website id) — stringified, since posthog-js keys are
            // strings.
            ph.group('company', websiteId)

            // Fallback attribution path: stamp the website id as a super
            // property (on every event) AND onto the person, so events can
            // still be tied to a company in the edge cases where the group is
            // momentarily absent (e.g. the first events of a session before
            // group() has run). Frontend-only fallback — the backend stamps
            // only $groups.company, not a website_id property.
            ph.register({ website_id: websiteId })
            ph.setPersonProperties({ website_id: websiteId })

            groupedWebsiteId = websiteId
        }
    }

    ph.register({
        workspace_lifecycle_stage: site.subscription_status,
        workspace_age_days: site.created_at_ts
            ? Math.floor((Date.now() / 1000 - site.created_at_ts) / 86400)
            : null,
        plan: site.plan,
    })
}

function setupInertiaTracking() {
    if (!posthogInstance) return

    // Track page views on Inertia navigation
    router.on('navigate', event => {
        const page = event.detail.page

        // Skip tracking when staff is impersonating
        const admin = page.props.admin as
            | { is_impersonating?: boolean }
            | undefined
        syncImpersonationState(admin?.is_impersonating ?? false)
        if (impersonationActive) {
            return
        }

        const url = window.location.href
        const path = window.location.pathname

        // Update user identification if user data changes — BEFORE capturing,
        // since group()/register() write super props that capture() reads at
        // call time: the pageview landing a workspace switch must already
        // carry the new workspace's $groups and context.
        const user = page.props.user as PostHogUser | undefined

        if (user?.id) {
            // Handles both a workspace SWITCH (re-groups to the new company)
            // and a re-login after a reset (re-establishes identity + group).
            identifyUserWithContext(posthogInstance, user)
        } else if (identifiedUserId) {
            // Logged out in-SPA: clear identity, the company group, and person
            // props so they never leak to the next user on a shared browser.
            // reset() also wipes super properties, so re-register the channel
            // stamp that every browser event must carry. (Full-load logouts are
            // caught by the reset-on-init check in initPostHog.)
            posthogInstance.reset()
            posthogInstance.register({ channel: 'web' })
            identifiedUserId = null
            groupedWebsiteId = null
        }

        // Capture page view with additional context
        posthogInstance.capture('$pageview', {
            $current_url: url,
            $pathname: path,
            component: page.component,
            props: page.props,
        })

        // Track specific page views
        trackSpecificPageViews(path, page.component)
    })

    // Track onboarding completion
    router.on('success', event => {
        if (impersonationActive) {
            return
        }
        const path = window.location.pathname
        if (path === '/onboarding/success') {
            posthogInstance.capture('onboarding_completed')
        }
    })
}

function trackSpecificPageViews(path: string, component: string) {
    // Track onboarding steps
    if (path.includes('/onboarding/')) {
        const step = path.split('/').pop()
        posthogInstance?.capture('onboarding_step_viewed', {
            step: step,
            component: component,
        })
    }

    // Track feature page views (see featureForPath for the route map).
    const feature = featureForPath(path)
    if (feature !== null) {
        posthogInstance?.capture('Feature Viewed', {
            feature: feature,
        })
    }
}

// Maps a pathname to the canonical ProductFeature it represents. Only routes
// that map to a canonical ProductFeature are emitted — non-registry pages
// (dashboard, leads, settings, team, billing) rely on $pageview instead, so
// the feature vocabulary stays aligned with the backend registry. Exported
// for tests, which pin these patterns to the real route shapes.
export function featureForPath(path: string): ProductFeature | null {
    const featurePages: Array<[RegExp, ProductFeature]> = [
        [/^\/[^/]+\/segments(\/|$)/, 'segments'],
        [/^\/[^/]+\/automations(\/|$)/, 'automations'],
        [/^\/[^/]+\/analytics(\/|$)/, 'reports'],
        [/^\/[^/]+\/settings\/integrations(\/|$)/, 'integrations'],
        // The custom-events settings page lives at the root level (no
        // /{website} prefix — see routes/web.php custom-event-sources.page).
        [/^\/settings\/custom-events(\/|$)/, 'custom_events'],
    ]

    for (const [pattern, feature] of featurePages) {
        if (pattern.test(path)) {
            return feature
        }
    }

    return null
}

// Export functions for manual event tracking
export function trackEvent(
    eventName: string,
    properties?: Record<string, any>
) {
    if (impersonationActive) {
        return
    }
    if (!posthogInstance) {
        console.warn('PostHog not initialized')
        return
    }
    posthogInstance.capture(eventName, properties)
}

export function identifyUser(userId: string, properties?: Record<string, any>) {
    if (impersonationActive) {
        return
    }
    if (!posthogInstance) {
        console.warn('PostHog not initialized')
        return
    }
    posthogInstance.identify(userId, properties)
}

export function setUserGroup(
    groupType: string,
    groupId: string,
    properties?: Record<string, any>
) {
    if (impersonationActive) {
        return
    }
    if (!posthogInstance) {
        console.warn('PostHog not initialized')
        return
    }
    posthogInstance.group(groupType, groupId, properties)
}

export function trackOnboardingProgress(
    step: string,
    data?: Record<string, any>
) {
    trackEvent('onboarding_progress', {
        step,
        ...data,
    })
}

export function trackFeatureUsage(
    feature: ProductFeature,
    action: string,
    properties?: Record<string, any>
) {
    trackEvent('Feature Used', {
        feature,
        action,
        ...properties,
    })
}

// For feature views that happen without a navigation (client-side drawers,
// panels) and so never pass through the pathname-based featureForPath map.
// Same canonical event/property shape as trackSpecificPageViews emits.
export function trackFeatureViewed(feature: ProductFeature) {
    trackEvent('Feature Viewed', { feature })
}

// Vue plugin
export const PostHogPlugin = {
    install(app: App, options: PostHogConfig) {
        const instance = initPostHog(options)

        if (instance) {
            app.config.globalProperties.$posthog = instance
            app.provide('posthog', instance)
        }
    },
}

// Composable for using PostHog in components
export function usePostHog() {
    return {
        trackEvent,
        identifyUser,
        setUserGroup,
        trackOnboardingProgress,
        trackFeatureUsage,
        posthog: posthogInstance,
    }
}
